/**
 * useChat — Hook für das interne Kommunikations-System (WhatsApp-Style)
 * 
 * Features:
 * - Chat-Räume laden (Direkt + Gruppen)
 * - Nachrichten laden, senden, löschen
 * - Dateien senden (alle Typen, große Dateien)
 * - Nachrichten als gelesen markieren
 * - Neuen Chat erstellen (Direkt oder Gruppe)
 * - Aufgabe aus Nachricht erstellen
 * - Realtime-Subscription für neue Nachrichten
 * - Benachrichtigungen erstellen
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ChatRoom {
  id: string;
  name: string | null;
  type: 'direct' | 'group';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Computed
  participants: ChatParticipant[];
  last_message?: ChatMessage | null;
  unread_count: number;
}

export interface ChatParticipant {
  id: string;
  chat_room_id: string;
  mitarbeiter_id: string;
  joined_at: string;
  // Joined
  mitarbeiter?: {
    id: string;
    vorname: string;
    nachname: string;
    initialen: string;
    rolle: string;
    avatar_url: string | null;
    is_online: boolean;
  };
}

export interface ChatMessage {
  id: string;
  chat_room_id: string;
  sender_id: string;
  content: string | null;
  message_type: 'text' | 'file' | 'image' | 'aufgabe';
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  file_type: string | null;
  aufgabe_id: string | null;
  read_by: string[];
  created_at: string;
  // Neue Felder für Bearbeiten/Löschen/Antworten
  edited_at: string | null;
  is_deleted: boolean;
  reply_to: string | null;
  // Joined
  sender?: {
    id: string;
    vorname: string;
    nachname: string;
    initialen: string;
    avatar_url: string | null;
  };
  // Joined: Referenz auf die beantwortete Nachricht
  reply_message?: ChatMessage | null;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useChat() {
  const { profile, impersonateUserId, teamMembers } = useAuth();
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const subscriptionRef = useRef<any>(null);
  const activeChatIdRef = useRef<string | null>(null);

  // Bei Impersonation die ID des impersonierten Users verwenden
  const currentUserId = impersonateUserId || profile?.id;

  // Effektives Profil: Bei Impersonation die Daten des impersonierten Users verwenden
  const effectiveProfile = (() => {
    if (impersonateUserId) {
      const target = teamMembers.find(m => m.id === impersonateUserId);
      if (target) {
        return {
          name: `${target.vorname} ${target.nachname}`,
          vorname: target.vorname,
          nachname: target.nachname,
          initialen: `${target.vorname?.[0] || ''}${target.nachname?.[0] || ''}`.toUpperCase(),
        };
      }
    }
    return {
      name: profile?.name || '',
      vorname: profile?.name?.split(' ')[0] || '',
      nachname: profile?.name?.split(' ').slice(1).join(' ') || '',
      initialen: profile?.initialen || '',
    };
  })();

  // ─── Chat-Räume laden ─────────────────────────────────────────────────────

  const loadChatRooms = useCallback(async () => {
    if (!currentUserId) return;
    setLoading(true);
    try {
      // 1. Alle Chat-Räume laden, an denen der User teilnimmt
      const { data: participations, error: pErr } = await supabase
        .from('chat_participants')
        .select('chat_room_id')
        .eq('mitarbeiter_id', currentUserId);

      if (pErr) throw pErr;
      if (!participations || participations.length === 0) {
        setChatRooms([]);
        setLoading(false);
        return;
      }

      const roomIds = participations.map(p => p.chat_room_id);

      // 2. Chat-Räume mit Participants laden
      const { data: rooms, error: rErr } = await supabase
        .from('chat_rooms')
        .select('*')
        .in('id', roomIds)
        .order('updated_at', { ascending: false });

      if (rErr) throw rErr;

      // 3. Alle Participants für diese Räume laden
      const { data: allParticipants, error: apErr } = await supabase
        .from('chat_participants')
        .select('*')
        .in('chat_room_id', roomIds);

      if (apErr) throw apErr;

      // 4. Mitarbeiter-Daten laden
      const mitarbeiterIds = Array.from(new Set((allParticipants || []).map(p => p.mitarbeiter_id)));
      const { data: mitarbeiterData } = await supabase
        .from('mitarbeiter')
        .select('id, vorname, nachname, rolle, avatar_url')
        .in('id', mitarbeiterIds);

      // initialen clientseitig berechnen (existiert nicht als DB-Spalte)
      const enrichedMitarbeiter = (mitarbeiterData || []).map(m => ({
        ...m,
        initialen: `${m.vorname?.[0] || ''}${m.nachname?.[0] || ''}`.toUpperCase(),
        is_online: false,
      }));
      const mitarbeiterMap = new Map(enrichedMitarbeiter.map(m => [m.id, m]));

      // 5. Letzte Nachricht pro Raum laden
      const { data: lastMessages } = await supabase
        .from('chat_messages')
        .select('*')
        .in('chat_room_id', roomIds)
        .order('created_at', { ascending: false });

      // Gruppiere nach Raum und nimm die neueste
      const lastMsgMap = new Map<string, ChatMessage>();
      for (const msg of (lastMessages || [])) {
        if (!lastMsgMap.has(msg.chat_room_id)) {
          lastMsgMap.set(msg.chat_room_id, msg);
        }
      }

      // 6. Unread-Count berechnen
      const unreadMap = new Map<string, number>();
      for (const msg of (lastMessages || [])) {
        if (msg.sender_id !== currentUserId && !(msg.read_by || []).includes(currentUserId)) {
          unreadMap.set(msg.chat_room_id, (unreadMap.get(msg.chat_room_id) || 0) + 1);
        }
      }

      // 7. Zusammenbauen
      const enrichedRooms: ChatRoom[] = (rooms || []).map(room => ({
        ...room,
        participants: (allParticipants || [])
          .filter(p => p.chat_room_id === room.id)
          .map(p => ({
            ...p,
            mitarbeiter: mitarbeiterMap.get(p.mitarbeiter_id) || undefined,
          })),
        last_message: lastMsgMap.get(room.id) || null,
        unread_count: unreadMap.get(room.id) || 0,
      }));

      // Sortieren: neueste Nachricht zuerst
      enrichedRooms.sort((a, b) => {
        const aTime = a.last_message?.created_at || a.updated_at;
        const bTime = b.last_message?.created_at || b.updated_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      setChatRooms(enrichedRooms);
    } catch (err) {
      console.error('Fehler beim Laden der Chat-Räume:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // ─── Nachrichten laden ────────────────────────────────────────────────────

  const loadMessages = useCallback(async (chatRoomId: string) => {
    setMessagesLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_room_id', chatRoomId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Sender-Daten laden
      const senderIds = Array.from(new Set((data || []).map(m => m.sender_id)));
      const { data: senders } = await supabase
        .from('mitarbeiter')
        .select('id, vorname, nachname, avatar_url')
        .in('id', senderIds);

      const enrichedSenders = (senders || []).map(s => ({
        ...s,
        initialen: `${s.vorname?.[0] || ''}${s.nachname?.[0] || ''}`.toUpperCase(),
      }));
      const senderMap = new Map(enrichedSenders.map(s => [s.id, s]));

      // Reply-Nachrichten auflösen
      const msgMap = new Map((data || []).map((m: any) => [m.id, m]));

      const enrichedMessages: ChatMessage[] = (data || []).map((msg: any) => {
        const replyMsg = msg.reply_to ? msgMap.get(msg.reply_to) : null;
        return {
          ...msg,
          read_by: msg.read_by || [],
          edited_at: msg.edited_at || null,
          is_deleted: msg.is_deleted || false,
          reply_to: msg.reply_to || null,
          sender: senderMap.get(msg.sender_id) || undefined,
          reply_message: replyMsg ? {
            ...replyMsg,
            read_by: replyMsg.read_by || [],
            edited_at: replyMsg.edited_at || null,
            is_deleted: replyMsg.is_deleted || false,
            reply_to: replyMsg.reply_to || null,
            sender: senderMap.get(replyMsg.sender_id) || undefined,
          } : null,
        };
      });

      setMessages(enrichedMessages);
    } catch (err) {
      console.error('Fehler beim Laden der Nachrichten:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // ─── Nachricht senden ─────────────────────────────────────────────────────

  const sendMessage = useCallback(async (
    chatRoomId: string,
    content: string,
    messageType: 'text' | 'file' | 'image' | 'aufgabe' = 'text',
    fileData?: { url: string; name: string; size: number; type: string },
    aufgabeId?: string
  ) => {
    if (!currentUserId) return null;
    setSending(true);
    try {
      const insertData: Record<string, any> = {
        chat_room_id: chatRoomId,
        sender_id: currentUserId,
        content,
        message_type: messageType,
        read_by: [currentUserId],
      };

      if (fileData) {
        insertData.file_url = fileData.url;
        insertData.file_name = fileData.name;
        insertData.file_size = fileData.size;
        insertData.file_type = fileData.type;
      }

      if (aufgabeId) {
        insertData.aufgabe_id = aufgabeId;
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Chat-Raum updated_at aktualisieren
      await supabase
        .from('chat_rooms')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', chatRoomId);

      // Benachrichtigungen für andere Teilnehmer erstellen
      const room = chatRooms.find(r => r.id === chatRoomId);
      if (room) {
        const otherParticipants = room.participants
          .filter(p => p.mitarbeiter_id !== currentUserId)
          .map(p => p.mitarbeiter_id);

        if (otherParticipants.length > 0) {
          const senderName = effectiveProfile.name || 'Unbekannt';
          const notifTitle = room.type === 'group' 
            ? `Neue Nachricht in ${room.name || 'Gruppe'}`
            : `Neue Nachricht von ${senderName}`;
          const notifMessage = messageType === 'file' 
            ? `${senderName} hat eine Datei gesendet: ${fileData?.name || 'Datei'}`
            : content?.substring(0, 100) || 'Neue Nachricht';

          const notifications = otherParticipants.map(mid => ({
            mitarbeiter_id: mid,
            type: 'chat',
            title: notifTitle,
            message: notifMessage,
            link: `/kommunikation?chat=${chatRoomId}`,
            is_read: false,
          }));

          await supabase.from('notifications').insert(notifications);
        }
      }

      // Nachricht zur lokalen Liste hinzufügen
      const enrichedMsg: ChatMessage = {
        ...data,
        read_by: data.read_by || [currentUserId],
        edited_at: null,
        is_deleted: false,
        reply_to: data.reply_to || null,
        reply_message: null,
        sender: {
          id: currentUserId,
          vorname: effectiveProfile.vorname,
          nachname: effectiveProfile.nachname,
          initialen: effectiveProfile.initialen,
          avatar_url: null,
        },
      };
      setMessages(prev => [...prev, enrichedMsg]);

      // Chat-Räume neu laden für aktualisierten Timestamp
      loadChatRooms();

      return data;
    } catch (err) {
      console.error('Fehler beim Senden:', err);
      return null;
    } finally {
      setSending(false);
    }
  }, [currentUserId, chatRooms, effectiveProfile, loadChatRooms]);

  // ─── Datei hochladen ──────────────────────────────────────────────────────

  const uploadFile = useCallback(async (file: File): Promise<{ url: string; name: string; size: number; type: string } | null> => {
    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `chat/${timestamp}_${safeName}`;

      const { data, error } = await supabase.storage
        .from('chat-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        // Bucket existiert möglicherweise nicht — versuche es mit dem public bucket
        console.error('Upload-Fehler:', error.message);
        // Fallback: Base64-Encoding für kleine Dateien
        if (file.size < 5 * 1024 * 1024) { // < 5MB
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                url: reader.result as string,
                name: file.name,
                size: file.size,
                type: file.type,
              });
            };
            reader.readAsDataURL(file);
          });
        }
        throw error;
      }

      const { data: urlData } = supabase.storage
        .from('chat-files')
        .getPublicUrl(data.path);

      return {
        url: urlData.publicUrl,
        name: file.name,
        size: file.size,
        type: file.type,
      };
    } catch (err) {
      console.error('Datei-Upload fehlgeschlagen:', err);
      return null;
    }
  }, []);

  // ─── Nachricht als gelesen markieren ──────────────────────────────────────

  const markAsRead = useCallback(async (chatRoomId: string) => {
    if (!currentUserId) return;
    try {
      // Alle ungelesenen Nachrichten in diesem Raum finden
      const { data: unread } = await supabase
        .from('chat_messages')
        .select('id, read_by')
        .eq('chat_room_id', chatRoomId)
        .neq('sender_id', currentUserId);

      if (!unread) return;

      // Nur Nachrichten updaten, die noch nicht gelesen sind
      for (const msg of unread) {
        const readBy = msg.read_by || [];
        if (!readBy.includes(currentUserId)) {
          await supabase
            .from('chat_messages')
            .update({ read_by: [...readBy, currentUserId] })
            .eq('id', msg.id);
        }
      }

      // Lokalen Unread-Count aktualisieren
      setChatRooms(prev => prev.map(r => 
        r.id === chatRoomId ? { ...r, unread_count: 0 } : r
      ));
    } catch (err) {
      console.error('Fehler beim Markieren als gelesen:', err);
    }
  }, [currentUserId]);

  // ─── Neuen Chat erstellen ─────────────────────────────────────────────────

  const createChat = useCallback(async (
    participantIds: string[],
    name?: string,
    type: 'direct' | 'group' = 'direct'
  ): Promise<string | null> => {
    if (!currentUserId) return null;
    try {
      // Bei Direkt-Chat: prüfe ob bereits ein Chat existiert
      if (type === 'direct' && participantIds.length === 1) {
        const existingRoom = chatRooms.find(r => {
          if (r.type !== 'direct') return false;
          const pIds = r.participants.map(p => p.mitarbeiter_id);
          return pIds.includes(currentUserId) && pIds.includes(participantIds[0]);
        });
        if (existingRoom) return existingRoom.id;
      }

      // Chat-Raum erstellen
      const { data: room, error: rErr } = await supabase
        .from('chat_rooms')
        .insert({
          name: name || null,
          type,
          created_by: currentUserId,
        })
        .select()
        .single();

      if (rErr) throw rErr;

      // Teilnehmer hinzufügen (inkl. sich selbst)
      const allParticipants = [currentUserId, ...participantIds.filter(id => id !== currentUserId)];
      const participantInserts = allParticipants.map(mid => ({
        chat_room_id: room.id,
        mitarbeiter_id: mid,
      }));

      const { error: pErr } = await supabase
        .from('chat_participants')
        .insert(participantInserts);

      if (pErr) throw pErr;

      // Räume neu laden
      await loadChatRooms();

      return room.id;
    } catch (err) {
      console.error('Fehler beim Erstellen des Chats:', err);
      return null;
    }
  }, [currentUserId, chatRooms, loadChatRooms]);

  // ─── Nachricht löschen (Soft-Delete) ───────────────────────────────────────────────────

  const deleteMessage = useCallback(async (messageId: string) => {
    try {
      // Soft-Delete: Nachricht als gelöscht markieren, nicht physisch löschen
      const { error } = await supabase
        .from('chat_messages')
        .update({ is_deleted: true, content: null, file_url: null, file_name: null })
        .eq('id', messageId);

      if (error) {
        // Fallback: Falls is_deleted-Spalte nicht existiert, hard delete
        const { error: delErr } = await supabase
          .from('chat_messages')
          .delete()
          .eq('id', messageId);
        if (delErr) throw delErr;
        setMessages(prev => prev.filter(m => m.id !== messageId));
        return;
      }

      // Lokal aktualisieren
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, is_deleted: true, content: null, file_url: null, file_name: null } : m
      ));
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  }, []);

  // ─── Nachricht bearbeiten ─────────────────────────────────────────────────────────

  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    try {
      const updateData: Record<string, any> = {
        content: newContent,
        edited_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('chat_messages')
        .update(updateData)
        .eq('id', messageId);

      if (error) throw error;

      // Lokal aktualisieren
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, content: newContent, edited_at: updateData.edited_at } : m
      ));
    } catch (err) {
      console.error('Fehler beim Bearbeiten:', err);
    }
  }, []);

  // ─── Antwort senden (mit reply_to Referenz) ─────────────────────────────────

  const sendReply = useCallback(async (
    chatRoomId: string,
    content: string,
    replyToId: string
  ) => {
    if (!currentUserId) return null;
    setSending(true);
    try {
      const insertData: Record<string, any> = {
        chat_room_id: chatRoomId,
        sender_id: currentUserId,
        content,
        message_type: 'text',
        read_by: [currentUserId],
        reply_to: replyToId,
      };

      const { data, error } = await supabase
        .from('chat_messages')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Chat-Raum updated_at aktualisieren
      await supabase
        .from('chat_rooms')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', chatRoomId);

      // Referenz auf die beantwortete Nachricht finden
      const replyMsg = messages.find(m => m.id === replyToId) || null;

      const enrichedMsg: ChatMessage = {
        ...data,
        read_by: data.read_by || [currentUserId],
        edited_at: null,
        is_deleted: false,
        reply_to: replyToId,
        reply_message: replyMsg,
        sender: {
          id: currentUserId,
          vorname: effectiveProfile.vorname,
          nachname: effectiveProfile.nachname,
          initialen: effectiveProfile.initialen,
          avatar_url: null,
        },
      };
      setMessages(prev => [...prev, enrichedMsg]);
      loadChatRooms();
      return data;
    } catch (err) {
      console.error('Fehler beim Antworten:', err);
      return null;
    } finally {
      setSending(false);
    }
  }, [currentUserId, messages, effectiveProfile, loadChatRooms]);

  // ─── Aufgabe aus Nachricht erstellen ──────────────────────────────────────────
  const createAufgabeFromMessage = useCallback(async (
    message: ChatMessage,
    titel?: string
  ): Promise<string | null> => {
    if (!currentUserId) return null;
    try {
      const aufgabenTitel = titel || message.content?.substring(0, 100) || 'Aufgabe aus Chat';
      
      const { data, error } = await supabase
        .from('aufgaben')
        .insert({
          titel: aufgabenTitel,
          beschreibung: message.content || '',
          status: 'offen',
          prioritaet: 'mittel',
          erstellt_von: currentUserId,
          zugewiesen_an: currentUserId,
          business_unit: 'gutachten',
        })
        .select('id')
        .single();

      if (error) throw error;

      // Referenz in der Nachricht speichern
      await supabase
        .from('chat_messages')
        .update({ aufgabe_id: data.id, message_type: 'aufgabe' })
        .eq('id', message.id);

      // Lokale Nachricht aktualisieren
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, aufgabe_id: data.id, message_type: 'aufgabe' as const } : m
      ));

      return data.id;
    } catch (err) {
      console.error('Fehler beim Erstellen der Aufgabe:', err);
      return null;
    }
  }, [currentUserId]);

  // ─── activeChatId Ref synchron halten ────────────────────────────────────

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  // ─── Realtime Subscription (stabil, wird nur einmal erstellt) ──────────────

  useEffect(() => {
    if (!currentUserId) return;

    // Subscription für neue und geänderte Nachrichten
    const channel = supabase
      .channel(`chat-messages-${currentUserId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
      }, (payload) => {
        const newMsg = payload.new as ChatMessage;

        // Nachricht in den aktiven Chat einfügen (wenn dieser Chat gerade offen ist)
        if (newMsg.chat_room_id === activeChatIdRef.current && newMsg.sender_id !== currentUserId) {
          // Sender-Daten nachladen
          supabase
            .from('mitarbeiter')
            .select('id, vorname, nachname, avatar_url')
            .eq('id', newMsg.sender_id)
            .single()
            .then(({ data: sender }) => {
              const enrichedSender = sender ? {
                ...sender,
                initialen: `${sender.vorname?.[0] || ''}${sender.nachname?.[0] || ''}`.toUpperCase(),
              } : undefined;
              setMessages(prev => {
                // Duplikat-Check: Nachricht nicht doppelt einfügen
                if (prev.some(m => m.id === newMsg.id)) return prev;
                return [...prev, {
                  ...newMsg,
                  read_by: newMsg.read_by || [],
                  edited_at: newMsg.edited_at || null,
                  is_deleted: newMsg.is_deleted || false,
                  reply_to: newMsg.reply_to || null,
                  sender: enrichedSender,
                }];
              });
            });

          // Automatisch als gelesen markieren wenn Chat offen ist
          const readBy = newMsg.read_by || [];
          if (!readBy.includes(currentUserId)) {
            supabase
              .from('chat_messages')
              .update({ read_by: [...readBy, currentUserId] })
              .eq('id', newMsg.id)
              .then(() => {});
          }
        }

        // Chat-Räume IMMER aktualisieren (für Unread-Count und letzte Nachricht)
        loadChatRooms();
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'chat_messages',
      }, (payload) => {
        const updatedMsg = payload.new as any;
        // Lokal aktualisieren (Bearbeiten, Löschen, Aufgabe-Referenz, read_by)
        setMessages(prev => prev.map(m => {
          if (m.id !== updatedMsg.id) return m;
          return {
            ...m,
            content: updatedMsg.content,
            edited_at: updatedMsg.edited_at || null,
            is_deleted: updatedMsg.is_deleted || false,
            message_type: updatedMsg.message_type || m.message_type,
            aufgabe_id: updatedMsg.aufgabe_id || m.aufgabe_id,
            file_url: updatedMsg.file_url,
            file_name: updatedMsg.file_name,
            read_by: updatedMsg.read_by || m.read_by,
          };
        }));

        // Chat-Räume aktualisieren (Unread-Count könnte sich geändert haben)
        loadChatRooms();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[Chat] Realtime-Subscription aktiv');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[Chat] Realtime-Subscription Fehler — versuche erneut...');
          // Automatischer Reconnect nach 3 Sekunden
          setTimeout(() => {
            supabase.removeChannel(channel);
            // Re-subscribe wird durch useEffect-Cleanup/Re-run getriggert
          }, 3000);
        }
      });

    subscriptionRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  // Nur von currentUserId abhängig — NICHT von activeChatId (nutzt Ref stattdessen)
  }, [currentUserId, loadChatRooms]);

  // ─── Initial laden ────────────────────────────────────────────────────────

  useEffect(() => {
    loadChatRooms();
  }, [loadChatRooms]);

  // ─── Chat öffnen ──────────────────────────────────────────────────────────

  const openChat = useCallback(async (chatRoomId: string) => {
    setActiveChatId(chatRoomId);
    await loadMessages(chatRoomId);
    await markAsRead(chatRoomId);
  }, [loadMessages, markAsRead]);

  // ─── Gesamte ungelesene Nachrichten ───────────────────────────────────────

  const totalUnread = chatRooms.reduce((sum, r) => sum + r.unread_count, 0);

  return {
    chatRooms,
    messages,
    activeChatId,
    loading,
    messagesLoading,
    sending,
    totalUnread,
    openChat,
    sendMessage,
    sendReply,
    editMessage,
    uploadFile,
    createChat,
    deleteMessage,
    createAufgabeFromMessage,
    markAsRead,
    loadChatRooms,
    setActiveChatId,
  };
}

// ─── Notifications Hook ─────────────────────────────────────────────────────

export function useNotifications() {
  const { profile, impersonateUserId } = useAuth();
  const effectiveUserId = impersonateUserId || profile?.id;
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = useCallback(async () => {
    if (!effectiveUserId) return;
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('mitarbeiter_id', effectiveUserId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Benachrichtigungen:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (err) {
      console.error('Fehler:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!effectiveUserId) return;
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('mitarbeiter_id', effectiveUserId)
        .eq('is_read', false);

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Fehler:', err);
    }
  }, [effectiveUserId]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Realtime Subscription
  useEffect(() => {
    if (!effectiveUserId) return;

    const channel = supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `mitarbeiter_id=eq.${effectiveUserId}`,
      }, (payload) => {
        const n = payload.new as any;
        setNotifications(prev => [n, ...prev]);
        if (n && n.type !== 'chat') {
          toast(n.title || 'Benachrichtigung', { description: n.message || undefined });
          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              new Notification(n.title || 'Gutachter Koop', { body: n.message || '', icon: '/favicon.svg' });
            }
          } catch { /* ignore */ }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [effectiveUserId]);

  // Einzelne Benachrichtigung löschen
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  }, []);

  // Alle Benachrichtigungen löschen
  const deleteAll = useCallback(async () => {
    if (!effectiveUserId) return;
    try {
      await supabase
        .from('notifications')
        .delete()
        .eq('mitarbeiter_id', effectiveUserId);

      setNotifications([]);
    } catch (err) {
      console.error('Fehler beim Löschen aller:', err);
    }
  }, [effectiveUserId]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAll,
    loadNotifications,
  };
}
